import WizardProgressBar from '@/components/shared/WizardProgressBar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import OrderSummarySidebar from '@/components/wizard/OrderSummarySidebar';
import { getWizardProgress, saveWizardProgress } from '@/lib/dataService';
import { formatRegionMoney, getActiveRegionConfig } from '@/lib/regionConfig';
import {
  TAX_COMPUTATION_ID,
  TAX_COMPUTATION_LATER,
  TAX_COMPUTATION_NOW,
  needsTaxComputationForFiling,
} from '@/lib/taxComputation';
import { OWN_IXBRL_ID, offersOwnIxbrl } from '@/lib/ownIxbrl';
import { ArrowRight, Calendar, CheckCircle, Clock, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Pricing per account type (pence for backend, pounds for display)
const ACCOUNT_TYPES = [
  { value: 'micro',  label: 'Micro-Entity',    subtitle: 'FRS 105 — turnover under £632k',  price: 25 },
  { value: 'small',  label: 'Small Company',   subtitle: 'FRS 102 Section 1A — most common', price: 50 },
  { value: 'full',   label: 'Standard / Full', subtitle: 'FRS 102 Full — complex accounts',  price: 75 },
];

// Generate last 5 financial years dynamically from today
const buildFinancialYears = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => current - i);
};

// A UK order may include Companies House and HMRC filing together; other regions keep choosing one.
const REGIONS_WITH_SEVERAL_FILINGS = new Set(['UK']);

// '48h' -> '48 hours'; anything else (e.g. 'standard') has no fixed turnaround to state.
const spellOutHours = (hours) => {
  const match = /^(\d+)h$/.exec(hours || '');
  return match ? `${match[1]} hours` : null;
};

const Step1Options = () => {
  const navigate = useNavigate();
  const financialYears = buildFinancialYears();
  const regionConfig = getActiveRegionConfig();
  const accountTypes = regionConfig.accountTypes;
  const allowsSeveralFilings = REGIONS_WITH_SEVERAL_FILINGS.has(regionConfig.code);

  const offersOwn = offersOwnIxbrl(regionConfig.code);
  const [solutions, setSolutions] = useState({
    ...Object.fromEntries(regionConfig.optionalServices.map((service) => [service.id, false])),
    ...(offersOwn ? { [OWN_IXBRL_ID]: false } : {}),
  });
  const [config, setConfig] = useState({
    financialYear: String(financialYears[1]), // default to previous year (most recently completed)
    accountType: null,                        // null = not selected (filing-only orders don't need iXBRL tagging)
    deliveryTime: 'standard'
  });

  useEffect(() => {
    async function restore() {
      let savedProgress = null;
      try { savedProgress = await getWizardProgress(); } catch (err) {
        console.error('Failed to load wizard progress:', err);
      }
      if (savedProgress?.solutions) {
        setSolutions(savedProgress.solutions);
        if (savedProgress.config) setConfig(prev => ({ ...prev, ...savedProgress.config }));
        toast.info('Restored your saved progress');
      }
      // Also check sessionStorage for existing wizard data
      const existingData = JSON.parse(sessionStorage.getItem('ixbrl_wizard') || '{}');
      if (existingData.solutions) setSolutions(existingData.solutions);
      if (existingData.config) setConfig(prev => ({ ...prev, ...existingData.config }));
    }
    restore();
  }, []);

  // Where the region allows several filing services on one order they toggle independently; elsewhere they are
  // mutually exclusive: picking one clears the rest, picking it again clears it.
  const selectOptionalService = (id) => {
    setSolutions((prev) => Object.fromEntries(
      Object.keys(prev).map((key) => [
        key,
        key === id ? !prev[id] : (allowsSeveralFilings ? prev[key] : false),
      ])
    ));
  };

  // Filing your own iXBRL and ordering tagging exclude each other: choosing one clears the other.
  const ownMode = !!solutions[OWN_IXBRL_ID];
  const chooseAccountsMode = (own) => {
    setSolutions((prev) => ({ ...prev, [OWN_IXBRL_ID]: own }));
    if (own) setConfig((prev) => ({ ...prev, accountType: null }));
  };

  // HMRC filing needs a tax computation in iXBRL: it is added with the filing, free, and asked about here.
  const asksForTaxComputation = needsTaxComputationForFiling(
    Object.keys(solutions).filter((key) => solutions[key]),
    regionConfig.code
  );

  const handleSaveProgress = async () => {
    const wizardData = { solutions, config, step: 1, savedAt: new Date().toISOString() };
    await saveWizardProgress(wizardData);
    toast.success('Progress saved! You can continue later from Dashboard.');
  };

  const handleContinue = async () => {
    // Tax Computation is never chosen on its own: an older saved selection is dropped and it follows HMRC filing.
    const selectedOptional = Object.keys(solutions).filter(key => solutions[key] && key !== TAX_COMPUTATION_ID);
    if (!config.accountType && selectedOptional.length === 0) {
      toast.error('Please select an account type for iXBRL tagging, or choose at least one filing service.');
      return;
    }
    const filingSelected = selectedOptional.includes('hmrc_filing') || selectedOptional.includes('companies_house_filing');
    if (selectedOptional.includes(OWN_IXBRL_ID) && !filingSelected) {
      toast.error('Filing your own iXBRL needs Companies House or HMRC filing. Please choose a filing service.');
      return;
    }
    if (filingSelected && !config.accountType && !selectedOptional.includes(OWN_IXBRL_ID)) {
      toast.error('Companies House and HMRC filing require iXBRL Financial Statements tagging. Please select an iXBRL tagging service before continuing.');
      return;
    }
    const services = [...selectedOptional];
    if (asksForTaxComputation) services.push(TAX_COMPUTATION_ID);
    if (config.accountType && regionConfig.primaryService && !services.includes(regionConfig.primaryService)) {
      services.unshift(regionConfig.primaryService);
    }
    const wizardData = {
      optionalServices: services,
      region: regionConfig.code,
      sourceSite: regionConfig.sourceSite,
      primaryService: regionConfig.primaryService || null,
      // accountType is the per-company default in Step 2; taxComputationTiming only means something with HMRC filing
      config: asksForTaxComputation
        ? { ...config, taxComputationTiming: config.taxComputationTiming || TAX_COMPUTATION_NOW }
        : { ...config, taxComputationTiming: undefined },
      step: 1,
      savedAt: new Date().toISOString()
    };
    sessionStorage.setItem('ixbrl_wizard', JSON.stringify(wizardData));
    try { await saveWizardProgress(wizardData); } catch (e) { /* non-blocking */ }
    navigate('/wizard/companies');
  };

  const optionalServices = [
    {
      id: 'hmrc_filing',
      title: 'HMRC CT600 iXBRL Filing',
      description: 'We submit your tagged CT600 corporate tax return directly to HMRC',
      price: '£50'
    },
    {
      id: 'companies_house_filing',
      title: 'Companies House iXBRL Filing',
      description: 'We submit your iXBRL accounts to Companies House on your behalf',
      price: '£25'
    }
  ];
  const regionalOptionalServices = regionConfig.optionalServices;

  // Wizard data for sidebar preview — pass accountType so sidebar shows price estimate
  // The summary reads the ordered services as a list, the same shape Continue saves.
  const previewServices = [
    ...Object.keys(solutions).filter((key) => solutions[key] && key !== TAX_COMPUTATION_ID),
    ...(asksForTaxComputation ? [TAX_COMPUTATION_ID] : []),
  ];
  const wizardData = { solutions, config, optionalServices: previewServices };

  return (
    <div data-testid="step1-options">
      <div className="page-header">
        <h1 className="page-title">Create New Job</h1>
        <p className="page-subtitle">Step 1 of 4: Configure common options</p>
      </div>

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1">
          {/* 1. Your accounts: have us tag them, or file iXBRL you already have */}
          <div className="bg-card border border-border rounded-lg p-6 mb-4">
            <h3 className="text-lg font-bold mb-1">1. Your accounts</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {offersOwn
                ? 'Do you need us to tag your accounts, or do you already have them as iXBRL?'
                : 'Select the standard that applies to your accounts.'}
            </p>
            {offersOwn && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" role="radiogroup" aria-label="Accounts">
                {[
                  { own: false, title: 'Tag my accounts', text: 'Send us PDF, Word or Excel accounts and we tag them as iXBRL.' },
                  { own: true, title: 'I already have iXBRL accounts', text: 'You upload the finished iXBRL. We check it and a reviewer approves it before it is filed. No tagging charge.' },
                ].map((mode) => (
                  <div
                    key={mode.title}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      ownMode === mode.own ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-accent'
                    }`}
                    onClick={() => chooseAccountsMode(mode.own)}
                    role="radio"
                    aria-checked={ownMode === mode.own}
                    data-testid={mode.own ? `optional-service-${OWN_IXBRL_ID}` : 'accounts-mode-tag'}
                  >
                    <span className="font-semibold text-sm text-foreground">{mode.title}</span>
                    <p className="text-xs text-muted-foreground mt-1">{mode.text}</p>
                  </div>
                ))}
              </div>
            )}
            {!ownMode && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {accountTypes.map((type) => (
                  <div
                    key={type.value}
                    className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      config.accountType === type.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40 hover:bg-accent'
                    }`}
                    onClick={() => setConfig({ ...config, accountType: type.value })}
                    role="radio"
                    aria-checked={config.accountType === type.value}
                  >
                    {config.accountType === type.value && (
                      <CheckCircle className="absolute top-3 right-3 w-4 h-4 text-primary" />
                    )}
                    <span className="font-semibold text-sm text-foreground">{type.label}</span>
                    <span className="text-xs text-muted-foreground mt-0.5 mb-3">{type.subtitle}</span>
                    <span className="text-2xl font-bold text-primary">{formatRegionMoney(type.price, regionConfig)}</span>
                    <span className="text-xs text-muted-foreground">per entity</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 2. Filing */}
          <div className="bg-card border border-border rounded-lg p-6 mb-4">
            <h3 className="text-lg font-semibold mb-1">2. Filing</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {ownMode ? 'Choose where we file your iXBRL.' : 'Want us to file for you?'} {allowsSeveralFilings ? 'Choose one or both.' : 'Choose one.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {regionalOptionalServices.map((option) => (
                <div
                  key={option.id}
                  className={`flex items-start space-x-3 p-4 border rounded-lg transition-colors cursor-pointer ${
                    solutions[option.id]
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-accent'
                  }`}
                  onClick={() => selectOptionalService(option.id)}
                  role="checkbox"
                  aria-checked={!!solutions[option.id]}
                  data-testid={`optional-service-${option.id}`}
                >
                  <span
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                      solutions[option.id] ? 'border-primary' : 'border-input'
                    }`}
                  >
                    {solutions[option.id] && <CheckCircle className="h-3 w-3 text-primary" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-medium text-sm">{option.title}</h4>
                      <span className="text-base font-bold text-primary whitespace-nowrap">{formatRegionMoney(option.price, regionConfig)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* HMRC needs a tax computation in iXBRL; we convert it free of charge */}
            {asksForTaxComputation && (
              <div className="mt-4 border border-border rounded-lg p-4 bg-muted/30" data-testid="tax-computation-question">
                <h4 className="font-semibold text-sm">HMRC filing needs a tax computation</h4>
                <p className="text-xs text-muted-foreground mt-1 mb-3">
                  A CT600 is filed with a tax computation in iXBRL. We convert yours to iXBRL for you free of charge, with no extra fee.
                  How will you provide it?
                </p>
                <RadioGroup
                  value={config.taxComputationTiming || TAX_COMPUTATION_NOW}
                  onValueChange={(val) => setConfig({ ...config, taxComputationTiming: val })}
                  className="space-y-2"
                >
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value={TAX_COMPUTATION_NOW} id="tax-now" className="mt-0.5" data-testid="tax-timing-now" />
                    <Label htmlFor="tax-now" className="cursor-pointer text-sm">
                      <span className="font-medium">I will upload it with my documents</span>
                      <span className="block text-xs text-muted-foreground">You add it on the upload step, after payment.</span>
                    </Label>
                  </div>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value={TAX_COMPUTATION_LATER} id="tax-later" className="mt-0.5" data-testid="tax-timing-later" />
                    <Label htmlFor="tax-later" className="cursor-pointer text-sm">
                      <span className="font-medium">I will send it separately</span>
                      <span className="block text-xs text-muted-foreground">
                        Email it to {regionConfig.supportEmail} quoting your job reference. HMRC filing cannot go ahead until we have it.
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </div>

          {/* Configuration Row - Horizontal Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Financial Year — last 5 years, computed dynamically */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4" />
                <Label className="text-sm font-medium">Financial Year</Label>
              </div>
              <Select value={config.financialYear} onValueChange={(val) => setConfig({...config, financialYear: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {financialYears.map((year) => (
                    <SelectItem key={year} value={String(year)}>FY {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Delivery Time */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4" />
                <Label className="text-sm font-medium">Delivery Time</Label>
              </div>
              <RadioGroup value={config.deliveryTime} onValueChange={(val) => setConfig({...config, deliveryTime: val})} className="space-y-2">
                <div className={`flex items-center space-x-2 p-2 border rounded cursor-pointer ${
                  config.deliveryTime === 'standard' ? 'border-primary bg-primary/5' : 'border-border'
                }`}>
                  <RadioGroupItem value="standard" id="standard-delivery" />
                  <Label htmlFor="standard-delivery" className="cursor-pointer text-sm flex-1">
                    <span className="font-medium">Standard</span>
                    {spellOutHours(regionConfig.delivery.standard.hours) && (
                      <span className="block text-xs text-muted-foreground">
                        Delivered within {spellOutHours(regionConfig.delivery.standard.hours)} &middot; included
                      </span>
                    )}
                  </Label>
                </div>
                <div className={`flex items-center space-x-2 p-2 border rounded cursor-pointer ${
                  config.deliveryTime === 'express' ? 'border-primary bg-primary/5' : 'border-border'
                }`}>
                  <RadioGroupItem value="express" id="express" />
                  <Label htmlFor="express" className="cursor-pointer text-sm flex-1">
                    <span className="font-medium">Express</span>
                    <span className="block text-xs text-muted-foreground">
                      {spellOutHours(regionConfig.delivery.express.hours) && `Delivered within ${spellOutHours(regionConfig.delivery.express.hours)} · `}
                      +{formatRegionMoney(regionConfig.delivery.express.surcharge, regionConfig)} per entity
                    </span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="flex justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/companies')}>Cancel</Button>
              <Button variant="ghost" onClick={handleSaveProgress}>
                <Save className="w-4 h-4 mr-2" />
                Save Progress
              </Button>
            </div>
            <Button onClick={handleContinue} data-testid="continue-btn">
              Continue to Company Selection
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Order Summary Sidebar - Top Aligned */}
        <div className="w-72 flex-shrink-0">
          <WizardProgressBar currentStep={1} />
          <OrderSummarySidebar wizardData={wizardData} currentStep={1} />
        </div>
      </div>
    </div>
  );
};

export default Step1Options;
